//
// Copyright (c) 2025 Comcast
//

import PropTypes from 'prop-types'
import { useEffect, useState, useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Link, createSearchParams } from 'react-router'
import styled from 'styled-components'

import { fetchTickets as fetchTicketsEndpoint } from 'api/tickets'

import { useLocationQueryParams } from 'utils/hooks'
import CardsListPanel from 'components/Shared/CardsListPanel/Index'
import colors from 'components/utils/colors'
import { RouteTicketingSystemsTickets } from 'routes'

import LoadingIndicator from 'components/Shared/LoadingIndicator/Index'
import FilterAndSearchRow from 'components/Shared/Skeleton/FilterAndSearchRow/Index'
import TicketsTable from 'components/App/TicketsPage/shared/TicketsTable/Index'

const StyledWrapper = styled.div`
  margin-top: 24px;
`

const PageSectionContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`

const PageSectionHeader = styled.div`
  font-family: Exo;
  font-size: 28px;
  font-weight: 300;
  line-height: 37px;
  padding-bottom: 19px;
  color: ${colors.secondary20};
`

const TicketsCount = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.secondary20};
`

const MoreLinkWrapper = styled.div`
  min-height: 16px;
  padding-top: 16px;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
  align-self: flex-end;
`

const PageSection = ({
  ticketingSystem, onTicketsLoaded,
  onTicketSelected, selectedTicketId, ticketBonding,
}) => {
  const { t } = useTranslation()

  const providerTicketBoards = useMemo(
    () => ticketingSystem?.providerTicketBoards || [],
    [ticketingSystem?.providerTicketBoards]
  )
  const [ticketsMap, setTicketsMap] = useState({})
  const [selectedBoardId, setSelectedBoardId] = useState(null)
  const [isFetching, setIsFetching] = useState(false)
  const dispatch = useDispatch()
  const [locationQueryParams] = useLocationQueryParams()
  const searchValue = locationQueryParams.search || ''

  const fetchTickets = useCallback(async ({ searchValue, providerTicketBoardId }) => {
    try {
      // subscribe: false keeps the parallel board requests independent — a shared useLazyQuery
      // trigger unsubscribes the previous call, and RTKQ >= 2.9.0 aborts unsubscribed requests
      const data = await dispatch(fetchTicketsEndpoint.initiate({
        ticketingSystemDisplayId: ticketingSystem.displayId,
        searchValue: searchValue,
        providerTicketBoardId: providerTicketBoardId,
      }, { subscribe: false, forceRefetch: true })).unwrap()

      return data || {}
    } catch (error) {
      return { tickets: [] }
    }
  }, [ticketingSystem.displayId, dispatch])

  useEffect(() => {
    const fetchAllBoardsTickets = async () => {
      setIsFetching(true)
      const fetchPromises = providerTicketBoards.map(board =>
        fetchTickets({
          searchValue,
          providerTicketBoardId: board.id,
        }).then(data => ({
          boardId: board.id,
          data,
        }))
      )
      try {
        const results = await Promise.all(fetchPromises)

        const newTicketsMap = {}
        results.forEach(result => {
          newTicketsMap[result.boardId] = result.data
        })

        setTicketsMap(prevMap => {
          return { ...prevMap, ...newTicketsMap }
        })

        if (selectedBoardId && newTicketsMap[selectedBoardId]) {
          const ticketCount = newTicketsMap[selectedBoardId]?.ticketsCount || 0
          onTicketsLoaded(ticketingSystem.id, ticketCount)
        }
      } catch (error) {
        console.error('Error fetching tickets for boards:', error)
      } finally {
        setIsFetching(false)
      }
    }
    fetchAllBoardsTickets()
  }, [providerTicketBoards, searchValue, fetchTickets]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selectedBoardId && providerTicketBoards.length > 0) {
      setSelectedBoardId(providerTicketBoards[0]?.id)
    }
  }, [selectedBoardId, providerTicketBoards])

  useEffect(() => {
    if (selectedBoardId && ticketsMap[selectedBoardId]) {
      const ticketCount = ticketsMap[selectedBoardId]?.ticketsCount || 0
      onTicketsLoaded(ticketingSystem.id, ticketCount)
    }
  }, [selectedBoardId, ticketsMap]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleBoardSelect = useCallback((board) => {
    setSelectedBoardId(board.id)
  }, [])

  const buildShowMoreLink = () => {
    const url = RouteTicketingSystemsTickets({ ticketingSystemDisplayId: ticketingSystem.displayId })
    const params = { search: searchValue }

    if (providerTicketBoards.length > 1) {
      params.provider_ticket_board_id = selectedBoardId
    }

    return `${url}?${createSearchParams(params).toString()}`
  }

  const currentBoard = useMemo(() =>
    selectedBoardId ? providerTicketBoards.find(board => board.id === selectedBoardId) : null
  , [selectedBoardId, providerTicketBoards])


  const currentBoardData = useMemo(() =>
    selectedBoardId ? ticketsMap[selectedBoardId] : ticketsMap.general || {}
  , [selectedBoardId, ticketsMap])

  const tickets = useMemo(() => currentBoardData?.tickets || [], [currentBoardData])
  const partnerStatusesMap = useMemo(() => currentBoardData?.partnerStatusesMap || {}, [currentBoardData])
  const totalTicketsCount = useMemo(() => currentBoardData?.ticketsCount || 0, [currentBoardData])

  const totalTicketsCountText = t('ticketsSearchResultsPage.ticketsFound', { ticketsCount: totalTicketsCount })
  const displayShowMoreLink = useMemo(() => currentBoardData?.totalPages > 1, [currentBoardData])

  useEffect(() => {
    if (
      providerTicketBoards.length > 0 &&
      selectedBoardId &&
      (!ticketsMap[selectedBoardId] || (ticketsMap[selectedBoardId]?.tickets?.length === 0))
    ) {
      const boardWithTickets = providerTicketBoards.find(board =>
        ticketsMap[board.id]?.tickets?.length > 0
      )
      if (boardWithTickets) {
        setSelectedBoardId(boardWithTickets.id)
      }
    }
  }, [ticketsMap, providerTicketBoards]) // eslint-disable-line react-hooks/exhaustive-deps

  const boardsWithTickets = useMemo(() => {
    return providerTicketBoards.filter(board => ticketsMap[board.id]?.tickets?.length > 0)
  }, [providerTicketBoards, ticketsMap])

  const showBoard = boardsWithTickets.length >= 1 && providerTicketBoards.length > 1
  return (
    <StyledWrapper data-test='ticket-search-section'>
      <PageSectionHeader data-test='ticket-search-section-header'>{ticketingSystem.name}</PageSectionHeader>
      <PageSectionContent>
        <LoadingIndicator loading={isFetching} small />
        <FilterAndSearchRow>
          <TicketsCount data-test='ticket-search-section-count'>{totalTicketsCountText}</TicketsCount>
        </FilterAndSearchRow>

        {showBoard && (
          <CardsListPanel
            onSelect={handleBoardSelect}
            cards={boardsWithTickets}
            selectedCardId={selectedBoardId}
            noPadding
            noUpperStyle
          />
        )}

        <TicketsTable
          tickets={tickets}
          ticketingSystem={ticketingSystem}
          selectedTicketId={selectedTicketId}
          onRowClick={onTicketSelected}
          partnerStatusesMap={partnerStatusesMap}
          searchValue={searchValue}
          ticketBonding={ticketBonding}
          providerTicketBoard={currentBoard}
        />

        <MoreLinkWrapper>
          {displayShowMoreLink && (
            <Link to={buildShowMoreLink()}>
              {t('ticketsSearchResultsPage.moreLink')}
            </Link>
          )}
        </MoreLinkWrapper>
      </PageSectionContent>
    </StyledWrapper>
  )
}

PageSection.propTypes = {
  ticketingSystem: PropTypes.object.isRequired,
  onTicketsLoaded: PropTypes.func.isRequired,
  onTicketSelected: PropTypes.func.isRequired,
  selectedTicketId: PropTypes.string,
  ticketBonding: PropTypes.string,
}

export default PageSection
